import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '../components/page-action-bar/page-action-bar';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  IStockItem,
  IStockItemSupplierLink,
  IStockLocation,
  IStockMovement,
  ISupplier,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const SLUG_RX = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// One page that handles both create and edit. Edit mode is identified by an
// id segment in the path (/stock/:id). Create mode lives at /stock/new.
// Stock has no "kind" discriminator after the redesign — every stock item
// is just stock, and any menu item can reference it via its recipe.
@customElement('stock-edit-page')
export class StockEditPage extends Base {
  @state() private _loading = true;
  @state() private _busy = false;
  @state() private _error: string | null = null;
  @state() private _saved = false;
  @state() private _existing: IStockItem | null = null;

  // Form fields. Per Sub-phase P.8 the purchase-unit pack + reorder qty
  // moved to per-supplier-link fields; this form only carries the
  // supplier-agnostic basics.
  @state() private _id = '';
  @state() private _name = '';
  @state() private _baseUnit = '';
  @state() private _reorderLevel = '';
  @state() private _locationId = '';
  @state() private _locations: IStockLocation[] = [];
  @state() private _isActive = true;

  // Optional starting on-hand at create time. Both fields must be filled
  // together (or both blank). Server validates and writes a single
  // initial_stock movement when set. Hidden in edit mode (you can't seed
  // an item that already has movements).
  @state() private _startingQty = '';
  @state() private _startingUnitCost = '';

  // Receive panel state.
  @state() private _movements: IStockMovement[] = [];
  @state() private _movementsLoading = false;

  // Sub-phase P.6 — supplier-links editor. Edit-mode only (the operator
  // can't link to a supplier on a stock item that doesn't exist yet).
  // _supplierLinks is the editable working copy; the full list is sent
  // to the dedicated PUT endpoint on save. _suppliers feeds the per-row
  // supplier picker.
  @state() private _supplierLinks: IStockItemSupplierLink[] = [];
  @state() private _suppliers: ISupplier[] = [];
  @state() private _supplierLinksDirty = false;
  @state() private _supplierLinksBusy = false;
  @state() private _supplierLinksError: string | null = null;
  @state() private _supplierLinksSaved = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 560px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      @media (max-width: 720px) {
        :host {
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
        }
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .actions {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-top: 8px;
      }
      .danger-zone {
        margin-top: 16px;
        padding: 12px;
        border: 1px dashed var(--sl-color-danger-300);
        border-radius: var(--sl-border-radius-medium);
      }
      .meta {
        font-size: 0.85rem;
        opacity: 0.7;
        display: flex;
        gap: 16px;
      }
      .receive-panel {
        margin-top: 16px;
      }
      .receive-row {
        display: grid;
        grid-template-columns: 1fr 1fr 2fr;
        gap: 12px;
        align-items: end;
      }
      .receive-actions {
        margin-top: 8px;
        display: flex;
        gap: 8px;
      }
      .movements {
        margin-top: 12px;
        font-size: 0.9rem;
      }
      .movement {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 8px;
        padding: 6px 0;
        border-bottom: 1px solid var(--sl-color-neutral-100);
      }
      .movement:last-child {
        border-bottom: none;
      }
      .delta-pos {
        color: var(--sl-color-success-700);
        font-variant-numeric: tabular-nums;
      }
      .delta-neg {
        color: var(--sl-color-danger-700);
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  // True once _init() has run end-to-end. Guards against re-init when
  // activeShop fires multiple times via context updates (it shouldn't,
  // but the property is reactive and we want this to be idempotent).
  private _initStarted = false;

  firstUpdated() {
    // Try to init now; if activeShop hasn't hydrated yet, updated() will
    // pick it up. Without this dual-trigger pattern, an F5 on /stock/:id
    // races the context provider and latches "No active shop" forever.
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;

    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }
    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can manage stock.';
      this._loading = false;
      return;
    }

    const editId = readEditIdFromPath();

    if (editId) {
      const api = ApiFactory.authenticated();
      const r = await api.getStockItem(this.activeShop.shopName, editId);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Stock item not found.';
        this._loading = false;
        return;
      }
      this._existing = r.data;
      this._id = r.data.id;
      this._name = r.data.name;
      this._baseUnit = r.data.baseUnit;
      this._reorderLevel = r.data.reorderLevel?.toString() ?? '';
      this._locationId = r.data.locationId ?? '';
      this._isActive = r.data.isActive;
      this._supplierLinks = r.data.supplierLinks.map((l) => ({ ...l }));
      void this._loadMovements();
      void this._loadSuppliers();
    }
    // Locations live independently of the item — load even in the
    // create flow so the picker can render.
    void this._loadLocations();
    this._loading = false;
  }

  private async _loadSuppliers() {
    if (!this.activeShop) return;
    const api = ApiFactory.authenticated();
    const r = await api.listSuppliers(this.activeShop.shopName);
    if (r.success && r.data) this._suppliers = r.data.filter((s) => s.isActive);
  }

  private async _loadLocations() {
    if (!this.activeShop) return;
    const api = ApiFactory.authenticated();
    const r = await api.listStockLocations(this.activeShop.shopName);
    if (r.success && r.data) this._locations = r.data.filter((l) => l.isActive);
  }

  private get _isEdit(): boolean {
    return this._existing !== null;
  }

  private _onInput(
    field:
      | 'id'
      | 'name'
      | 'baseUnit'
      | 'reorderLevel'
      | 'startingQty'
      | 'startingUnitCost'
  ) {
    return (e: Event) => {
      const v = (e.target as HTMLInputElement).value;
      if (field === 'id') this._id = v.toLowerCase().trim();
      if (field === 'name') this._name = v;
      if (field === 'baseUnit') this._baseUnit = v;
      if (field === 'reorderLevel') this._reorderLevel = v;
      if (field === 'startingQty') this._startingQty = v;
      if (field === 'startingUnitCost') this._startingUnitCost = v;
      this._saved = false;
    };
  }

  private async _save(e: Event) {
    e.preventDefault();
    if (this._busy || !this.activeShop) return;
    this._error = null;
    this._saved = false;

    const name = this._name.trim();
    const baseUnit = this._baseUnit.trim();
    if (!name || !baseUnit) {
      this._error = 'Name and unit are required.';
      return;
    }
    const reorderLevel =
      this._reorderLevel.trim() === ''
        ? undefined
        : parseFloat(this._reorderLevel);
    if (
      reorderLevel !== undefined &&
      (isNaN(reorderLevel) || reorderLevel < 0)
    ) {
      this._error = 'Reorder level must be a non-negative number.';
      return;
    }

    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      if (this._isEdit) {
        const r = await api.updateStockItem(
          this.activeShop.shopName,
          this._id,
          {
            name,
            baseUnit,
            reorderLevel: reorderLevel ?? null,
            // Empty string clears (Unsorted); a value assigns.
            locationId: this._locationId,
            isActive: this._isActive,
          }
        );
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Save failed.';
          return;
        }
        this._existing = r.data;
        this._saved = true;
      } else {
        const id = this._id.trim();
        if (!id || !SLUG_RX.test(id)) {
          this._error =
            'Id must be 2-48 chars, lowercase letters/digits/hyphens, no leading/trailing hyphen.';
          return;
        }
        // Optional starting on-hand: both fields filled or both blank.
        const startingQtyStr = this._startingQty.trim();
        const startingUnitCostStr = this._startingUnitCost.trim();
        if ((startingQtyStr === '') !== (startingUnitCostStr === '')) {
          this._error =
            'Starting quantity and starting unit cost must both be provided, or both left blank.';
          return;
        }
        let startingQty: number | undefined;
        let startingUnitCost: number | undefined;
        if (startingQtyStr !== '') {
          startingQty = parseFloat(startingQtyStr);
          startingUnitCost = parseFloat(startingUnitCostStr);
          if (
            !Number.isFinite(startingQty) ||
            startingQty <= 0 ||
            !Number.isFinite(startingUnitCost) ||
            startingUnitCost <= 0
          ) {
            this._error =
              'Starting quantity and starting unit cost must both be greater than zero.';
            return;
          }
        }
        const r = await api.createStockItem(this.activeShop.shopName, {
          id,
          name,
          baseUnit,
          reorderLevel,
          locationId: this._locationId || undefined,
          startingQty,
          startingUnitCost,
        });
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Create failed.';
          return;
        }
        // Land back on the list to see it.
        void router.navigate(resolveRouterPath('stock'));
      }
    } catch (err) {
      this._error = (err as Error)?.message ?? 'Unexpected error.';
    } finally {
      this._busy = false;
    }
  }

  private async _loadMovements() {
    if (!this._existing || !this.activeShop) return;
    this._movementsLoading = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.listStockMovements(
        this.activeShop.shopName,
        this._existing.id,
        30
      );
      if (r.success && r.data) this._movements = r.data;
    } finally {
      this._movementsLoading = false;
    }
  }

  private _renderMovements() {
    if (this._movementsLoading && this._movements.length === 0) {
      return html`<p class="movements">Loading history…</p>`;
    }
    if (this._movements.length === 0) {
      return html`<p class="movements"><em>No movements yet.</em></p>`;
    }
    return html`
      <div class="movements">
        ${this._movements.slice(0, 20).map(
          (m) => html`
            <div class="movement">
              <span class=${m.delta >= 0 ? 'delta-pos' : 'delta-neg'}>
                ${m.delta >= 0 ? '+' : ''}${m.delta}
                ${this._existing?.baseUnit ?? ''}
              </span>
              <span> ${m.reason}${m.note ? ` — ${m.note}` : ''} </span>
              <small>${new Date(m.occurredUtc).toLocaleString()}</small>
            </div>
          `
        )}
      </div>
    `;
  }

  private async _deactivate() {
    if (!this._existing || !this.activeShop) return;
    if (
      !confirm(
        `Deactivate "${this._existing.name}"? You can reactivate it later.`
      )
    )
      return;
    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.deactivateStockItem(
        this.activeShop.shopName,
        this._existing.id
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Deactivate failed.';
        return;
      }
      this._existing = r.data;
      this._isActive = r.data.isActive;
      this._saved = true;
    } finally {
      this._busy = false;
    }
  }

  private _back() {
    void router.navigate(resolveRouterPath('stock'));
  }

  private _addSupplierLink() {
    const taken = new Set(this._supplierLinks.map((l) => l.supplierId));
    const available = this._suppliers.find((s) => !taken.has(s.id));
    // Sensible defaults: PurchaseUnit defaults to the BaseUnit ("buy in
    // the same unit you use") with conversion 1. Operator can change to
    // "2L bottle" / "carton-5x2L" / etc. Cost / reorder qty / min order
    // qty all start blank so the operator types real values.
    this._supplierLinks = [
      ...this._supplierLinks,
      {
        supplierId: available?.id ?? '',
        supplierSku: null,
        purchaseUnit: this._baseUnit || 'each',
        baseUnitsPerPurchaseUnit: 1,
        purchaseUnitCost: null,
        reorderQty: null,
        minOrderQty: null,
      },
    ];
    this._supplierLinksDirty = true;
    this._supplierLinksSaved = false;
  }

  private _removeSupplierLink(idx: number) {
    this._supplierLinks = this._supplierLinks.filter((_, i) => i !== idx);
    this._supplierLinksDirty = true;
    this._supplierLinksSaved = false;
  }

  private _moveSupplierLinkUp(idx: number) {
    if (idx === 0) return;
    const next = [...this._supplierLinks];
    const [row] = next.splice(idx, 1);
    next.splice(idx - 1, 0, row);
    this._supplierLinks = next;
    this._supplierLinksDirty = true;
    this._supplierLinksSaved = false;
  }

  private _editSupplierLink(
    idx: number,
    patch: Partial<IStockItemSupplierLink>
  ) {
    this._supplierLinks = this._supplierLinks.map((l, i) =>
      i === idx ? { ...l, ...patch } : l
    );
    this._supplierLinksDirty = true;
    this._supplierLinksSaved = false;
  }

  private async _saveSupplierLinks() {
    if (!this._existing || !this.activeShop || this._supplierLinksBusy) return;
    this._supplierLinksError = null;
    this._supplierLinksSaved = false;

    // Client-side preflight matches the server's checks so we surface
    // the specific row a user typed wrong rather than a generic "already
    // listed" / "must be > 0" coming back from the API.
    const seen = new Set<string>();
    for (let i = 0; i < this._supplierLinks.length; i++) {
      const row = this._supplierLinks[i];
      const label = `Row ${i + 1}`;
      if (!row.supplierId) {
        this._supplierLinksError = `${label}: pick a supplier.`;
        return;
      }
      if (seen.has(row.supplierId)) {
        this._supplierLinksError = `${label}: this supplier is already listed.`;
        return;
      }
      seen.add(row.supplierId);
      if (!row.purchaseUnit || !row.purchaseUnit.trim()) {
        this._supplierLinksError = `${label}: pack name (e.g. "2L bottle") is required.`;
        return;
      }
      if (
        !Number.isFinite(row.baseUnitsPerPurchaseUnit) ||
        row.baseUnitsPerPurchaseUnit <= 0
      ) {
        this._supplierLinksError = `${label}: pack size in ${this._baseUnit || 'base units'} must be greater than zero.`;
        return;
      }
      if (
        row.purchaseUnitCost !== null &&
        row.purchaseUnitCost !== undefined &&
        (!Number.isFinite(row.purchaseUnitCost) || row.purchaseUnitCost <= 0)
      ) {
        this._supplierLinksError = `${label}: cost per pack must be greater than zero, or leave it blank.`;
        return;
      }
      if (
        row.reorderQty !== null &&
        row.reorderQty !== undefined &&
        (!Number.isFinite(row.reorderQty) || row.reorderQty <= 0)
      ) {
        this._supplierLinksError = `${label}: reorder qty must be greater than zero, or leave it blank.`;
        return;
      }
      if (
        row.minOrderQty !== null &&
        row.minOrderQty !== undefined &&
        (!Number.isFinite(row.minOrderQty) || row.minOrderQty <= 0)
      ) {
        this._supplierLinksError = `${label}: minimum order qty must be greater than zero, or leave it blank.`;
        return;
      }
    }

    this._supplierLinksBusy = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.updateStockItemSupplierLinks(
        this.activeShop.shopName,
        this._existing.id,
        this._supplierLinks
      );
      if (!r.success || !r.data) {
        this._supplierLinksError = r.error ?? 'Save failed.';
        return;
      }
      this._existing = r.data;
      this._supplierLinks = r.data.supplierLinks.map((l) => ({ ...l }));
      this._supplierLinksDirty = false;
      this._supplierLinksSaved = true;
    } finally {
      this._supplierLinksBusy = false;
    }
  }

  private _renderSupplierLinks() {
    if (!this._isEdit) return nothing;
    const noSuppliersConfigured = this._suppliers.length === 0;
    return html`
      <sl-card class="receive-panel">
        <div slot="header">
          <strong>Suppliers</strong>
          <p style="margin: 4px 0 0 0; opacity: 0.75; font-size: 0.85rem;">
            Link this stock item to one or more suppliers so the
            <em>draft POs from low-stock</em> helper knows where to order from.
            The first row is the primary supplier — its SKU goes on the PO at
            place time.
          </p>
        </div>

        ${
          noSuppliersConfigured
            ? html`
                <sl-alert variant="warning" open>
                  No suppliers configured for this shop yet. Add one on the
                  <a href=${resolveRouterPath('suppliers')}>Suppliers</a> page
                  first.
                </sl-alert>
              `
            : nothing
        }
        ${
          this._supplierLinks.length === 0
            ? html`
                <p style="opacity: 0.7; margin: 12px 0;">
                  <em
                    >No supplier links yet. Add one to enable the reorder helper
                    for this item.</em
                  >
                </p>
              `
            : html`
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  ${this._supplierLinks.map((row, idx) => this._renderSupplierLinkRow(row, idx))}
                </div>
              `
        }

        <div
          style="display: flex; gap: 8px; margin-top: 12px; align-items: center;"
        >
          <sl-button
            size="small"
            variant="default"
            ?disabled=${noSuppliersConfigured || this._supplierLinksBusy}
            @click=${() => this._addSupplierLink()}
          >
            <sl-icon slot="prefix" name="plus-lg"></sl-icon>
            Add supplier
          </sl-button>
          <sl-button
            size="small"
            variant="primary"
            ?loading=${this._supplierLinksBusy}
            ?disabled=${this._supplierLinksBusy || !this._supplierLinksDirty}
            @click=${() => this._saveSupplierLinks()}
          >
            Save suppliers
          </sl-button>
          ${
            this._supplierLinksSaved
              ? html`<sl-badge variant="success" pill>Saved</sl-badge>`
              : nothing
          }
        </div>

        ${
          this._supplierLinksError
            ? html`<sl-alert variant="danger" open style="margin-top: 8px;"
                >${this._supplierLinksError}</sl-alert
              >`
            : nothing
        }
      </sl-card>
    `;
  }

  private _renderSupplierLinkRow(row: IStockItemSupplierLink, idx: number) {
    // Live-derived "≈ $X per <baseUnit>" hint so the operator can sanity-
    // check whether they typed the right per-pack price. Computed from
    // whatever's currently in the form, not the saved value.
    const baseUnit = this._baseUnit || this._existing?.baseUnit || 'unit';
    const cost = row.purchaseUnitCost;
    const conv = row.baseUnitsPerPurchaseUnit;
    const perBase = cost && cost > 0 && conv && conv > 0 ? cost / conv : null;
    const perBaseHint =
      perBase !== null
        ? `≈ ${perBase.toFixed(perBase < 0.01 ? 6 : 4)} per ${baseUnit}`
        : '';

    return html`
      <sl-card
        style="border-left: 4px solid ${
          idx === 0
            ? 'var(--sl-color-primary-500)'
            : 'var(--sl-color-neutral-300)'
        };"
      >
        <div
          slot="header"
          style="display: flex; align-items: center; gap: 8px;"
        >
          <strong style="flex: 1;">
            ${idx === 0 ? 'Primary supplier' : `Supplier #${idx + 1}`}
          </strong>
          <sl-icon-button
            name="arrow-up"
            label="Promote to primary"
            ?disabled=${idx === 0 || this._supplierLinksBusy}
            @click=${() => this._moveSupplierLinkUp(idx)}
          ></sl-icon-button>
          <sl-icon-button
            name="trash"
            label="Remove"
            ?disabled=${this._supplierLinksBusy}
            @click=${() => this._removeSupplierLink(idx)}
          ></sl-icon-button>
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          <sl-select
            label="Supplier"
            .value=${row.supplierId}
            ?disabled=${this._supplierLinksBusy}
            @sl-change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement & { value: string })
                .value;
              this._editSupplierLink(idx, { supplierId: v });
            }}
          >
            ${this._suppliers.map(
              (s) => html`<sl-option value=${s.id}>${s.name}</sl-option>`
            )}
          </sl-select>

          <div class="row">
            <sl-input
              label="Pack name"
              help-text=${`e.g. "2L bottle", "carton-5x2L", "case-of-24"`}
              .value=${row.purchaseUnit}
              ?disabled=${this._supplierLinksBusy}
              @sl-input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                this._editSupplierLink(idx, { purchaseUnit: v });
              }}
            ></sl-input>
            <sl-input
              label=${`${baseUnit} per pack`}
              help-text=${`How many ${baseUnit} are in one pack`}
              type="number"
              min="0"
              step="any"
              .value=${
                row.baseUnitsPerPurchaseUnit > 0
                  ? row.baseUnitsPerPurchaseUnit.toString()
                  : ''
              }
              ?disabled=${this._supplierLinksBusy}
              @sl-input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                const n = v.trim() === '' ? 0 : parseFloat(v);
                this._editSupplierLink(idx, {
                  baseUnitsPerPurchaseUnit: Number.isFinite(n) ? n : 0,
                });
              }}
            ></sl-input>
          </div>

          <sl-input
            label=${`Cost per ${row.purchaseUnit || 'pack'}`}
            help-text=${perBaseHint || 'What the supplier charges for ONE pack'}
            type="number"
            min="0"
            step="any"
            .value=${row.purchaseUnitCost?.toString() ?? ''}
            ?disabled=${this._supplierLinksBusy}
            @sl-input=${(e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              this._editSupplierLink(idx, {
                purchaseUnitCost: v.trim() === '' ? null : parseFloat(v),
              });
            }}
          ></sl-input>

          <div class="row">
            <sl-input
              label=${`Reorder qty (${row.purchaseUnit || 'packs'})`}
              help-text="Used by the reorder helper to draft a PO line"
              type="number"
              min="0"
              step="any"
              .value=${row.reorderQty?.toString() ?? ''}
              ?disabled=${this._supplierLinksBusy}
              @sl-input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                this._editSupplierLink(idx, {
                  reorderQty: v.trim() === '' ? null : parseFloat(v),
                });
              }}
            ></sl-input>
            <sl-input
              label=${`Minimum order (${row.purchaseUnit || 'packs'})`}
              help-text="Optional — supplier's MOQ. Helper bumps up to this."
              type="number"
              min="0"
              step="any"
              .value=${row.minOrderQty?.toString() ?? ''}
              ?disabled=${this._supplierLinksBusy}
              @sl-input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                this._editSupplierLink(idx, {
                  minOrderQty: v.trim() === '' ? null : parseFloat(v),
                });
              }}
            ></sl-input>
          </div>

          <sl-input
            label="Supplier SKU (optional)"
            help-text="Their catalog code — goes on the PO"
            .value=${row.supplierSku ?? ''}
            ?disabled=${this._supplierLinksBusy}
            @sl-input=${(e: Event) => {
              const v = (e.target as HTMLInputElement).value;
              this._editSupplierLink(idx, {
                supplierSku: v.trim() === '' ? null : v,
              });
            }}
          ></sl-input>
        </div>
      </sl-card>
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    if (this._error && !this._existing && !this._id) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }

    return html`
      <sl-card>
        <div
          slot="header"
          style="display: flex; align-items: center; gap: 8px;"
        >
          <h2 style="margin: 0; flex: 1;">
            ${this._isEdit ? this._existing?.name : 'New stock item'}
          </h2>
        </div>

        ${
          this._isEdit && this._existing
            ? html`
                <div class="meta">
                  <span
                    ><strong>${this._existing.onHand}</strong>
                    ${this._existing.baseUnit} on hand</span
                  >
                  <span>cost: ${this._existing.cost}</span>
                </div>
                <sl-divider></sl-divider>
              `
            : nothing
        }

        <form @submit=${this._save}>
          <sl-input
            label="Id (slug)"
            help-text="Lowercase, hyphens. Cannot be changed later."
            .value=${this._id}
            ?disabled=${this._isEdit}
            @sl-input=${this._onInput('id')}
            required
          ></sl-input>

          <sl-input
            label="Name"
            help-text="The display name."
            .value=${this._name}
            @sl-input=${this._onInput('name')}
            required
          ></sl-input>

          <div class="row">
            <sl-input
              label="Unit"
              help-text="e.g. ml, g, each. The atomic unit recipes consume in."
              .value=${this._baseUnit}
              @sl-input=${this._onInput('baseUnit')}
              required
            ></sl-input>
            <sl-input
              label="Reorder level (optional)"
              type="number"
              min="0"
              step="any"
              .value=${this._reorderLevel}
              @sl-input=${this._onInput('reorderLevel')}
            ></sl-input>
          </div>

          <div class="row" style="grid-template-columns: 1fr;">
            <sl-select
              label="Stock location (optional)"
              help-text="Walk-order group for stocktake. Items without a location render under Unsorted."
              .value=${this._locationId}
              @sl-change=${(e: Event) => {
                this._locationId = (e.target as HTMLSelectElement).value;
                this._saved = false;
              }}
            >
              <sl-option value="">(Unsorted)</sl-option>
              ${this._locations.map(
                (l) => html`<sl-option value=${l.id}>${l.name}</sl-option>`
              )}
            </sl-select>
          </div>

          ${
            this._isEdit
              ? html`
                  <div class="row" style="grid-template-columns: 1fr;">
                    <label
                      style="display: flex; align-items: center; gap: 8px; cursor: pointer;"
                    >
                      <sl-switch
                        ?checked=${this._isActive}
                        @sl-change=${(e: Event) => {
                          this._isActive = (
                            e.target as HTMLInputElement
                          ).checked;
                          this._saved = false;
                        }}
                      ></sl-switch>
                      <span
                        ><strong>Active</strong> — uncheck to hide from menu
                        builders without losing history.</span
                      >
                    </label>
                  </div>
                `
              : html`
                  <fieldset
                    style="border: 1px solid var(--sl-color-neutral-200); border-radius: var(--sl-border-radius-medium); padding: 12px;"
                  >
                    <legend style="padding: 0 6px; font-weight: 600;">
                      Starting on-hand (optional)
                    </legend>
                    <p
                      style="margin: 0 0 8px 0; font-size: 0.85rem; opacity: 0.8;"
                    >
                      If you already have some of this item on the shelf, enter
                      the count and the per-unit cost. The system records this
                      as your initial stock. Leave blank to start at zero and
                      add stock later via a stock take or delivery.
                    </p>
                    <div class="row">
                      <sl-input
                        label="Starting quantity"
                        help-text="In the unit above"
                        type="number"
                        min="0"
                        step="any"
                        .value=${this._startingQty}
                        @sl-input=${this._onInput('startingQty')}
                      ></sl-input>
                      <sl-input
                        label="Starting unit cost"
                        help-text="Cost per unit you paid"
                        type="number"
                        min="0"
                        step="any"
                        .value=${this._startingUnitCost}
                        @sl-input=${this._onInput('startingUnitCost')}
                      ></sl-input>
                    </div>
                  </fieldset>
                `
          }
          ${
            this._error
              ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
              : nothing
          }
          ${
            this._saved
              ? html`<sl-alert variant="success" open closable
                  >Saved.</sl-alert
                >`
              : nothing
          }

          <page-action-bar>
            <sl-button
              slot="left"
              variant="default"
              @click=${this._back}
              ?disabled=${this._busy}
            >
              Cancel
            </sl-button>
            <sl-button
              slot="right"
              type="submit"
              variant="primary"
              ?loading=${this._busy}
              ?disabled=${this._busy}
            >
              ${this._isEdit ? 'Save changes' : 'Create'}
            </sl-button>
          </page-action-bar>
        </form>

        ${
          this._isEdit && this._existing?.isActive
            ? html`
                <div class="danger-zone">
                  <strong>Deactivate</strong>
                  <p style="margin: 4px 0 8px 0; opacity: 0.75;">
                    Hides this item but keeps its history. Past sales /
                    movements are unaffected. You can re-enable it later.
                  </p>
                  <sl-button
                    variant="warning"
                    @click=${this._deactivate}
                    ?disabled=${this._busy}
                  >
                    Deactivate
                  </sl-button>
                </div>
              `
            : nothing
        }
      </sl-card>

      ${this._renderSupplierLinks()}
      ${
        this._isEdit && this._existing
          ? html`
              <sl-card class="receive-panel">
                <div slot="header">
                  <strong>Recent movements</strong>
                  <p
                    style="margin: 4px 0 0 0; opacity: 0.75; font-size: 0.85rem;"
                  >
                    Stock changes for this item. New deliveries arrive via the
                    delivery flow (when supplier + PO surface ships); stock-take
                    variance lands here too.
                  </p>
                </div>
                ${this._renderMovements()}
              </sl-card>
            `
          : nothing
      }
    `;
  }
}

// Routes use plain paths (no query params) for the id, e.g. /stock/{id} for edit.
// We extract it from window.location.pathname.
function readEditIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/stock\/([^/?#]+)/);
  if (!m) return null;
  if (m[1] === 'new') return null;
  return decodeURIComponent(m[1]);
}
